import { type BoxProps } from '@ironhack/design-system2/components/box';
import { Flex } from '@ironhack/design-system2/components/flex';
import { Text, type TextProps } from '@ironhack/design-system2/components/text';
import { Wrap, WrapItem } from '@ironhack/design-system2/components/wrap';
import { type ReactElement, type RefObject } from 'react';
import { pluck } from 'ramda';

import { CmsImage } from '@/app/components/server/cms-image';
import { ModuleWrapper } from '@/app/components/client/module-wrapper';
import {
  type DatoCompany,
  type DatoImage,
  type DatoModuleFormat,
} from '@/lib/datocms/types';

export type Logo = { listLogo: Array<{ image: DatoImage }> };

export type LogoList = {
  headingProps?: TextProps;
  logos: DatoCompany[];
  moduleFormat: [DatoModuleFormat];
  subtitle?: string;
  title?: string;
};

type Props = {
  data: LogoList;
  ref?: RefObject<HTMLDivElement>;
} & BoxProps;

export const LogoList = (props: Props): ReactElement => {
  const {
    data: {
      headingProps = {},
      logos = [],
      moduleFormat: [moduleFormat],
      subtitle,
      title,
    },
    ref,
    title: navTitle,
    ...boxProps
  } = props;

  const images = pluck('horizontalTransparentLogo', logos);

  return (
    <ModuleWrapper
      innerProps={{
        align: 'center',
        direction: 'column',
        gap: 3,
        textAlign: ['left', 'center'],
      }}
      moduleFormat={moduleFormat}
      outerProps={boxProps}
      // Send ref and title directly for NavigationTab
      ref={ref}
      title={navTitle}
    >
      {(title || subtitle) && (
        <Flex direction="column" gap={1.5} width="100%">
          {title && (
            <Text as="h2" textStyle={['3xl', null, '4xl']} {...headingProps}>
              {title}
            </Text>
          )}
          {subtitle && (
            <Text
              // Make sure it's 60% width in desktop in spite of outer padding
              mx={[0, null, 'calc(20% - var(--chakra-space-6))']}
              textStyle={title ? 'l' : 'lBold'}
            >
              {subtitle}
            </Text>
          )}
        </Flex>
      )}

      <Wrap justify="center" spacingX={2} spacingY={3}>
        {images.map(
          (image) =>
            image && (
              <WrapItem alignItems="center" key={image.responsiveImage.alt}>
                <CmsImage data={image.responsiveImage} />
              </WrapItem>
            )
        )}
      </Wrap>
    </ModuleWrapper>
  );
};

export const logoListFragment = `
  fragment logoListFragment on LogoListRecord {
    logos {
      ...companyFragment
    }
    moduleFormat{
      ...moduleFormatFragment
    }
    subtitle
    title
  }
`;
